import styles from './HomePage.module.scss'
import { Button, Text } from '@percent/lemonade'
import {
  MarketplaceProgrammeVisitedSource,
  OriginCategory,
  PageViewSource
} from '@percent/cause-dashboard/common/hooks/useCausesDashboardAnalytics/marketplaceAnalytics.types'
import { ProfileCompletionProgress } from '@percent/cause-dashboard/app/home/profileCompletionProgress/ProfileCompletionProgress'
import { useAuthState, useCausesPortalAnalytics } from '@percent/cause-dashboard/common/hooks'
import RejectedImage from '@percent/cause-dashboard/assets/images/home/rejected-image.png'
import { useTranslation } from 'react-i18next'
import { HomePageHelpLinks } from './homepageHelpLinks/HomePageHelpLinks'

import { GrantFinderBanner } from '@percent/cause-dashboard/app/ai/GrantFinderBanner/GrantFinderBanner'
import { useBreadcrumbs } from '../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'
import { DiscoverDiscounts } from '@percent/cause-dashboard/common/components/discoverDiscounts/DiscoverDiscounts'
import { useCanUseGrants } from '@percent/cause-dashboard/common/hooks/canUseGrants/canUseGrants'
import { useHomePageStory } from '@percent/cause-dashboard/storyblok/home/useHomePageStory'
import { HomePageSpotlightBannerBridge } from './HomePageSpotlightBannerBridge'
import { GfmHomeBanner } from './gfmHomeBanner/GfmHomeBanner'
import { useGfmBannerVariant } from '@percent/cause-dashboard/common/hooks/useGfmBannerVariant/useGfmBannerVariant'

export const HomePage = () => {
  const { t } = useTranslation()
  useBreadcrumbs([{ label: t('breadcrumb.home') }])

  const { authState } = useAuthState()
  const { track } = useCausesPortalAnalytics()
  const canUseGrants = useCanUseGrants()
  const { isReady: isGfmVariantReady, variant: gfmVariant } = useGfmBannerVariant('home_banner')

  const { story: homePageStory } = useHomePageStory(true)

  const isRejected = !!authState.claimOrganisation?.rejectedAt

  const handleSpotlightCTAClick = (programmeOfferName: string) => {
    track('Dashboard spotlight CTA button clicked', {
      organisation: authState.organisation?.displayName,
      organisationId: authState.organisation?.id,
      clickedFrom: MarketplaceProgrammeVisitedSource.Banner,
      source: PageViewSource.Home,
      originCategory: OriginCategory.Dashboard,
      programmeOfferName,
      accountId: authState.user?.id
    })
  }

  return (
    <div className={styles.mainWrapper}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.wavingHandBackground}>
            <span className={styles.wavingHand} role="img" aria-label="waving hand">
              👋
            </span>{' '}
          </div>
          <span>{t('typography.home.title')}</span>
        </div>
        {isGfmVariantReady && gfmVariant && <GfmHomeBanner variant={gfmVariant} />}
        {isGfmVariantReady && !gfmVariant && !canUseGrants && <GrantFinderBanner />}
        {homePageStory && (
          <HomePageSpotlightBannerBridge initialStory={homePageStory} onCtaClick={handleSpotlightCTAClick} />
        )}
        {isRejected ? (
          <div>
            <div className={styles.descriptionCard}>
              <div className={styles.rejectedContainer}>
                <img className={styles.rejectedImage} src={RejectedImage} alt="" />
                <div className={styles.rejectedDescription}>
                  <Text className={styles.title}>
                    <span role="img" aria-label="sad face">
                      ☹️
                    </span>{' '}
                    {t('typography.home.rejected')}
                  </Text>
                  <Text>{t('typography.home.rejectedDescription')}</Text>
                  <Button variant="primary">{t('typography.home.rejectedButton')}</Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <DiscoverDiscounts showCarrousel />
        )}
      </div>

      <HomePageHelpLinks />

      <div className={styles.container}>
        <ProfileCompletionProgress />
      </div>
    </div>
  )
}
